import Constants from '@/Constants';
import { ContentRequest } from '@/models/ContentRequest';
import { CurrentLearningMapRequest } from '@/models/CurrentLearningMapRequest';
import { LessonRequestData } from '@/models/LessonRequestData';
import { analyticsService, PlaylistEvents } from '@/services/AnalyticsService';
import BotService from '@/services/BotService';
import { courseService } from '@/services/CourseService';
import { ApplicationState } from '@/store';
import { finishContentRequest, startContentRequest } from '@/store/item/action';
import { getCurrentLearningMapRequest } from '@/store/learningMap/action';
import { finishLessonRequest, startLessonRequest } from '@/store/list/action';
import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import './style.scss';

const MarkAsCompleted: React.FC = () => {
    const { content, contentEnrollment, courseEnrollment, classEnrollment, personId, courseName, course, learningMap, strings } = useSelector(
        (state: ApplicationState) => ({
            content: state.itens.currentData.content,
            contentEnrollment: state.itens.currentData.contentEnrollment,
            courseEnrollment: state.lists.currentData.courseEnrollment,
            classEnrollment: state.lists.currentData.classEnrollment,
            personId: state.identity.data.person.id,
            courseName: state.lists.currentData.course && state.lists.currentData.course.name,
            contentName: state.itens.currentData.content.name,
            course: state.lists.currentData.course,
            learningMap: state.learningMap.data.currentLearningMap,
            strings: state.locale.data,
        }),
    );

    const [isContentComplete, setIsContentComplete] = useState<boolean>();
    const [isCourseContent, setIsCourseContent] = useState<boolean>();
    const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
    const [notStartedYet, setNotStartedYet] = useState<boolean>(true);
    const [finish, setFinish] = useState<boolean>(false);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getCurrentLearningMapRequest({} as CurrentLearningMapRequest));
    }, []);

    const onCompletePressHandler = (event: SyntheticEvent): void => {
        event.preventDefault();

        if (isCourseContent) {
            dispatch(
                finishLessonRequest({
                    lessonId: parseInt(String(lessonId)),
                    personId,
                    courseId: parseInt(String(courseId)),
                    lessonStartAndFinishData: { classEnrollmentId: classEnrollment.id },
                } as LessonRequestData),
            );
            if (isContentComplete !== undefined && !isContentComplete) {
                analyticsService.logEvent(PlaylistEvents.FINISH_LIST_ITEM, {
                    entityId: parseInt(String(lessonId)),
                    entityName: content.name,
                    entityType: Constants.entityTypes.LESSON,
                });
            }
        } else {
            dispatch(finishContentRequest({ contentId: content.id, personId } as ContentRequest));
            if (isContentComplete !== undefined && !isContentComplete) {
                analyticsService.logEvent(PlaylistEvents.FINISH_ITEM, {
                    entityId: content.id as number,
                    entityName: content.name,
                    entityType: Constants.entityTypes.CONTENT,
                });
            }
        }
        setFinish(true);
    };

    useEffect(() => {
        if (!isContentComplete && finish && learningMap) {
            BotService.sendConsumedLearningMapNotification(dispatch, learningMap, Number(content.workload));
        }
    }, [finish, learningMap, isContentComplete]);

    const isContentCompleted = useCallback(() => {
        let isCompleted = false;

        if (isCourseContent) {
            const hasCourseEnrollment: boolean = !!courseEnrollment;
            isCompleted =
                hasCourseEnrollment && courseService.getLessonStatusIsCompleted(courseEnrollment.lessonEnrollments, parseInt(String(lessonId)));
        } else {
            const hasContentEnrollment: boolean = !!contentEnrollment;
            isCompleted = hasContentEnrollment && contentEnrollment.status === Constants.contentEnrollmentStatus.COMPLETED;
        }
        setIsContentComplete(isCompleted);
    }, [contentEnrollment, courseEnrollment, isCourseContent, lessonId]);

    useEffect(() => {
        setIsCourseContent(courseService.isCourseContent());
    }, [course]);

    useEffect(() => {
        if (isCourseContent !== undefined) {
            isContentCompleted();
        }
    }, [isContentCompleted, isCourseContent]);

    const onStartHandler = (): void => {
        if (notStartedYet && isCourseContent !== undefined) {
            setNotStartedYet(false);
            if (isCourseContent) {
                dispatch(
                    startLessonRequest({
                        lessonId: parseInt(String(lessonId)),
                        lessonStartAndFinishData: { classEnrollmentId: classEnrollment.id },
                        personId,
                    } as LessonRequestData),
                );

                analyticsService.logEvent(PlaylistEvents.ACCESS_ITEM, {
                    entityId: parseInt(String(lessonId)),
                    entityName: content.name,
                    entityType: Constants.entityTypes.LESSON,
                });

                analyticsService.logEvent(PlaylistEvents.CONSUMPTION_PLAYLIST, {
                    entityId: parseInt(String(courseId)),
                    entityName: courseName,
                    entityType: Constants.entityTypes.COURSE,
                });
            } else {
                dispatch(startContentRequest({ contentId: content.id } as ContentRequest));

                analyticsService.logEvent(PlaylistEvents.ACCESS_ITEM, {
                    entityId: content.id as number,
                    entityName: content.name,
                    entityType: Constants.entityTypes.CONTENT,
                });
            }
        }
    };

    useEffect(onStartHandler, [content.id, dispatch, isCourseContent, notStartedYet, lessonId]);

    return (
        <div className="completed-menu">
            <button className={`${isContentComplete && 'completed'}`} onClick={onCompletePressHandler}>
                {isContentComplete ? (
                    <>
                        <i className="material-icons">{Constants.icons.CHECK}</i>
                        {strings.CONCLUDED}
                    </>
                ) : (
                    <>{strings.MARK_AS_DONE}</>
                )}
            </button>

            <div>
                <span>{strings.WHY_SHOULD_I_MARK_IT_AS_DONE}</span>

                <OverlayTrigger
                    overlay={
                        <Tooltip id="mark-done-hint">
                            <span dangerouslySetInnerHTML={{ __html: strings.TOOLTIP_MARK_AS_DONE }}></span>
                        </Tooltip>
                    }
                    placement="bottom-end"
                >
                    <i className="material-icons">{Constants.icons.INFO_OUTLINE}</i>
                </OverlayTrigger>
            </div>
        </div>
    );
};

export default MarkAsCompleted;
